// @ts-nocheck
import helpers from '@/utils/helpers';
import { timeToMinutes, isTimeInRange } from './timeUtils';

interface Game {
  _id?: string;
  timeSlot?: string;
  homeTeam?: string;
  awayTeam?: string;
  division: string;
  date?: string;
  globalDate?: string; // MM-DD-YYYY format
  time: string;
  location?: string;
  locationModel?: string;
  slotSectionId?: string; // Track which slot section this game belongs to
  duration?: number; // Duration in minutes
}

interface TimeSlot {
  _id: string;
  location: {
    _id: string;
    name: string;
  };
  availableDate: string;
  startTime: string;
  endTime: string;
  maxGames: number;
  timeZone: string;
}

/**
 * Helper function to find matching timeSlot for a game
 */
export const findMatchingTimeSlot = (game: Game, timeSlots: TimeSlot[]): TimeSlot | null => {
  if (!game.globalDate || !game.time || !game.location) {
    return null;
  }

  const match = timeSlots.find(timeSlot => {
    // Check if dates match
    if (timeSlot.availableDate !== game.globalDate) {
      return false;
    }

    // Check location match (either by name or by locationModel)
    const locationMatches =
      game.location === timeSlot.location?.name ||
      game.locationModel === timeSlot.location?._id;

    if (!locationMatches) {
      return false;
    }

    // Check if game time overlaps with timeSlot time range
    const gameTime = game.time;
    const slotStartTime = timeSlot.startTime;
    const slotEndTime = timeSlot.endTime;

    // Convert times to comparable format (HH:MM)
    const isTimeOverlapping = isTimeInRange(gameTime, slotStartTime, slotEndTime);

    return isTimeOverlapping;
  });

  return match || null;
};

/**
 * Process games for timeSlot matching
 */
export const processGamesForTimeSlotMatching = (games: Game[], timeSlots: TimeSlot[]): Game[] => {
  return games.map(game => {
    let updatedGame = { ...game };

    // If game doesn't have a timeSlot, try to match it with existing timeSlots
    if (!game.timeSlot) {
      const matchingTimeSlot = findMatchingTimeSlot(game, timeSlots);
      if (matchingTimeSlot) {
        updatedGame = {
          ...game,
          timeSlot: matchingTimeSlot._id,
          locationModel: matchingTimeSlot.location?._id
        };
      }
    }

    return updatedGame;
  });
};

/**
 * Helper function to get games for a specific time and date
 */
export const getGamesForTimeAndDate = (gamesByDate: Record<string, Game[]>, timeSlot: string, date: string): Game[] => {
  const gamesForDate = gamesByDate[date] || [];
  const timeMinutes = timeToMinutes(timeSlot);

  // Find games that match this exact time (within 15 minutes tolerance)
  const matchingGames = gamesForDate.filter(game => {
    if (!game.time) return false;
    const gameTimeMinutes = timeToMinutes(game.time);
    return Math.abs(gameTimeMinutes - timeMinutes) <= 15; // Within 15 minutes
  });

  return matchingGames;
};

/**
 * Filter games by selected divisions
 */
export const filterGamesByDivisions = (games: Game[], selectedDivisionIds: string[]): Game[] => {
  if (selectedDivisionIds.length === 0) return [];

  return games.filter(game => {
    const gameDivisionId = helpers.getObjId(game.division);
    return selectedDivisionIds.includes(gameDivisionId);
  });
};
